import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Typography, Grid, Accordion, AccordionSummary, AccordionDetails, Button } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { Link } from 'react-router-dom';
import logo from '../assets/logo.png';
import { C, F, G, SH } from '../styles/tokens';

const SERVICES = [
  {
    title: 'ITR for Salaried Individuals',
    desc: 'File ITR-1 or ITR-2 for salary income, house property, and capital gains. Claim all deductions under 80C, 80D, HRA exemption, and standard deduction accurately.',
  },
  {
    title: 'Business & Professional Income',
    desc: 'ITR-3 and ITR-4 (Presumptive Scheme) for freelancers, consultants, and small businesses. We calculate your taxable income under 44ADA / 44AD where applicable.',
  },
  {
    title: 'Corporate Income Tax',
    desc: 'ITR-6 filing for private limited companies, LLPs, and partnerships. We handle MAT computations, advance tax, and TDS reconciliation.',
  },
  {
    title: 'NRI Income Tax Filing',
    desc: 'File income tax returns for Non-Resident Indians with Indian income from rent, capital gains, NRO/NRE interest, or business. DTAA benefit claimed wherever applicable.',
  },
  {
    title: 'Capital Gains Tax',
    desc: 'Accurate reporting of short-term and long-term capital gains from equity, mutual funds, property, and other assets. Proper computation of indexation benefits.',
  },
  {
    title: 'Tax Planning & Advisory',
    desc: 'Proactive tax planning to minimise your tax outgo legally. Investments under 80C, 80D, 80G, ELSS, PPF, NPS, and home loan interest optimised for your income slab.',
  },
];

const STEPS = [
  { step: '01', title: 'Income Assessment', desc: 'We review all your income sources — salary, business, rent, capital gains, and other income — to select the correct ITR form.' },
  { step: '02', title: 'Document Collection', desc: 'Form 16, AIS/TIS, bank statements, investment proofs, capital gains statements, and rent receipts collected and reviewed.' },
  { step: '03', title: 'Deduction Optimisation', desc: 'We identify every eligible deduction and exemption under Sections 80C, 80D, 24(b), HRA, LTA, and more to reduce your tax liability.' },
  { step: '04', title: 'Tax Computation', desc: 'We compute taxable income, advance tax paid vs. required, TDS credit, and final tax payable / refund under both old and new tax regimes.' },
  { step: '05', title: 'Return Preparation', desc: 'Your ITR is prepared accurately and reviewed before filing — no errors, no notices, no penalties.' },
  { step: '06', title: 'E-filing & Verification', desc: 'Filed on the IT portal and e-verified via Aadhaar OTP or net banking. Refund typically processed within 10–45 days.' },
];

const FAQS = [
  {
    q: 'Who is required to file an income tax return in India?',
    a: 'Individuals with gross total income exceeding ₹2.5 lakh per year (₹3 lakh for senior citizens, ₹5 lakh for super senior citizens) must file ITR. Additionally, those with foreign assets, high-value transactions, or professional income must file even if income is below the threshold.',
  },
  {
    q: 'What is the last date to file ITR in India?',
    a: 'The due date for filing ITR for individuals (non-audit cases) is typically 31st July of the assessment year. For businesses requiring audit, it is 31st October. Late filing is possible with a penalty under Section 234F (up to ₹5,000).',
  },
  {
    q: 'What is the difference between old tax regime and new tax regime?',
    a: 'The old tax regime allows deductions and exemptions (80C, HRA, LTA, etc.) and has higher slab rates. The new tax regime has lower slab rates but most deductions and exemptions are not available. The new regime is the default from FY 2023-24 onwards, but you can opt for the old regime while filing your return.',
  },
  {
    q: 'What happens if I don\'t file my income tax return?',
    a: 'Consequences include: penalty under Section 234F (up to ₹5,000), interest on unpaid tax, loss of carry-forward for capital losses, and potential scrutiny notice from the Income Tax Department.',
  },
  {
    q: 'Can I claim a refund if excess TDS was deducted?',
    a: 'Yes. If TDS deducted or advance tax paid is more than your actual tax liability, the excess appears as a refund in your ITR. Refunds are typically processed within 10–45 days of e-verification of the return.',
  },
  {
    q: 'Can NRIs file income tax returns from abroad?',
    a: 'Yes. NRIs with Indian-sourced income (salary, rent, capital gains, interest) must file ITR in India. Pracco handles NRI ITR filing remotely — no need to visit India. Double Taxation Avoidance Agreement (DTAA) benefits are claimed wherever applicable.',
  },
];

const IncomeTaxFilingPage = () => (
  <>
    <Helmet>
      <title>Income Tax Return (ITR) Filing India — Salaried, Business & NRI | Pracco</title>
      <meta name="description" content="File your ITR online with Pracco. Expert income tax return filing for salaried employees, freelancers, businesses & NRIs. Maximise refunds, claim all deductions. Free consultation — +91-9311817126." />
      <meta name="keywords" content="income tax return filing India, ITR filing online, ITR-1 ITR-2 ITR-3 filing, NRI income tax India, income tax filing for salaried, business income tax return, capital gains tax India, tax planning India, advance tax, TDS refund" />
      <link rel="canonical" href="https://pracco.in/income-tax-filing" />
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://pracco.in/" },
          { "@type": "ListItem", "position": 2, "name": "Income Tax Filing", "item": "https://pracco.in/income-tax-filing" },
        ],
      })}</script>
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "HowTo",
        "name": "How to File Income Tax Return in India",
        "description": "Step-by-step process to file your ITR accurately and on time in India.",
        "totalTime": "P3D",
        "step": STEPS.map((s) => ({
          "@type": "HowToStep",
          "position": parseInt(s.step),
          "name": s.title,
          "text": s.desc,
        })),
      })}</script>
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": FAQS.map(f => ({
          "@type": "Question",
          "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a },
        })),
      })}</script>
    </Helmet>

    {/* ── Mini Header ── */}
    <Box component="header" sx={{ px: { xs: 2, md: 6 }, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${C.border}`, background: C.white }}>
      <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
        <Box component="img" src={logo} alt="Pracco" sx={{ height: 36 }} />
      </Link>
      <Button
        component="a"
        href="https://wa.me/9311817126"
        target="_blank"
        rel="noopener noreferrer"
        startIcon={<WhatsAppIcon />}
        sx={{
          background: C.whatsapp,
          color: C.white,
          fontFamily: F.body,
          fontWeight: 600,
          fontSize: 13,
          px: 2.5,
          py: 1,
          borderRadius: 2,
          textTransform: 'none',
          '&:hover': { background: C.whatsappDk },
        }}
      >
        Free Consultation
      </Button>
    </Box>

    {/* ── Hero ── */}
    <Box
      component="section"
      sx={{
        background: 'linear-gradient(160deg, #fffbeb 0%, #F8FAFC 55%, #fef3c7 100%)',
        px: { xs: 2.5, md: 8 },
        py: { xs: 6, md: 9 },
        textAlign: 'center',
      }}
    >
      <Typography
        component="h1"
        sx={{ fontFamily: F.heading, fontSize: { xs: 28, md: 44 }, fontWeight: 800, color: C.text, lineHeight: 1.15, mb: 2 }}
      >
        Income Tax Return Filing in India
      </Typography>
      <Typography sx={{ fontFamily: F.body, fontSize: { xs: 15, md: 18 }, color: C.textMid, maxWidth: 680, mx: 'auto', lineHeight: 1.7, mb: 4 }}>
        File your ITR accurately and on time with Pracco. Salaried, business, NRI, and corporate returns.
        Maximise refunds, minimise tax liability. 100% online. Free consultation.
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Button
          component="a"
          href="https://wa.me/9311817126"
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<WhatsAppIcon />}
          sx={{
            background: C.amber,
            color: C.white,
            fontFamily: F.body,
            fontWeight: 700,
            fontSize: 15,
            px: 4,
            py: 1.5,
            borderRadius: 2.5,
            textTransform: 'none',
            boxShadow: SH.btnAmber,
            '&:hover': { background: C.amberDark, boxShadow: SH.btnAmberHover },
          }}
        >
          File My ITR Now
        </Button>
        <Button
          component={Link}
          to="/"
          variant="outlined"
          sx={{
            fontFamily: F.body,
            fontWeight: 600,
            fontSize: 14,
            px: 3,
            py: 1.5,
            borderRadius: 2.5,
            textTransform: 'none',
            borderColor: C.amber,
            color: C.amberDark,
            '&:hover': { background: C.amberLight },
          }}
        >
          View All Services
        </Button>
      </Box>
    </Box>

    {/* ── Service Types ── */}
    <Box component="section" sx={{ px: { xs: 2.5, md: 8 }, py: { xs: 6, md: 8 }, background: C.white }}>
      <Typography component="h2" sx={{ fontFamily: F.heading, fontSize: { xs: 22, md: 30 }, fontWeight: 800, color: C.text, mb: 1, textAlign: 'center' }}>
        Income Tax Filing Services
      </Typography>
      <Typography sx={{ fontFamily: F.body, fontSize: 15, color: C.textMid, textAlign: 'center', mb: 5, maxWidth: 560, mx: 'auto' }}>
        Expert ITR filing for every taxpayer category — salaried, self-employed, business, NRI, and corporate.
      </Typography>
      <Grid container spacing={3} sx={{ maxWidth: 1100, mx: 'auto' }}>
        {SERVICES.map((s) => (
          <Grid item xs={12} sm={6} md={4} key={s.title}>
            <Box sx={{ border: `1px solid ${C.border}`, borderRadius: 3, p: 3, height: '100%', transition: 'box-shadow 0.2s', '&:hover': { boxShadow: SH.cardHover } }}>
              <CheckCircleOutlineIcon sx={{ color: C.amber, mb: 1 }} />
              <Typography sx={{ fontFamily: F.heading, fontSize: 16, fontWeight: 700, color: C.text, mb: 1 }}>{s.title}</Typography>
              <Typography sx={{ fontFamily: F.body, fontSize: 14, color: C.textMid, lineHeight: 1.7 }}>{s.desc}</Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>

    {/* ── Process Steps ── */}
    <Box component="section" sx={{ px: { xs: 2.5, md: 8 }, py: { xs: 6, md: 8 }, background: C.bg }}>
      <Typography component="h2" sx={{ fontFamily: F.heading, fontSize: { xs: 22, md: 30 }, fontWeight: 800, color: C.text, mb: 1, textAlign: 'center' }}>
        How ITR Filing Works at Pracco
      </Typography>
      <Typography sx={{ fontFamily: F.body, fontSize: 15, color: C.textMid, textAlign: 'center', mb: 5, maxWidth: 560, mx: 'auto' }}>
        Simple, transparent, and fully handled by our tax experts — you just share documents.
      </Typography>
      <Grid container spacing={3} sx={{ maxWidth: 1000, mx: 'auto' }}>
        {STEPS.map((s) => (
          <Grid item xs={12} sm={6} md={4} key={s.step}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Typography sx={{ fontFamily: F.heading, fontSize: 34, fontWeight: 800, color: C.border, lineHeight: 1, minWidth: 44 }}>{s.step}</Typography>
              <Box>
                <Typography sx={{ fontFamily: F.heading, fontSize: 15, fontWeight: 700, color: C.text, mb: 0.5 }}>{s.title}</Typography>
                <Typography sx={{ fontFamily: F.body, fontSize: 13, color: C.textMid, lineHeight: 1.7 }}>{s.desc}</Typography>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>

    {/* ── FAQ ── */}
    <Box component="section" sx={{ px: { xs: 2.5, md: 8 }, py: { xs: 6, md: 8 }, background: C.white, maxWidth: 860, mx: 'auto' }}>
      <Typography component="h2" sx={{ fontFamily: F.heading, fontSize: { xs: 22, md: 30 }, fontWeight: 800, color: C.text, mb: 4, textAlign: 'center' }}>
        Frequently Asked Questions — Income Tax Filing
      </Typography>
      {FAQS.map((faq) => (
        <Accordion key={faq.q} disableGutters elevation={0} sx={{ border: `1px solid ${C.border}`, mb: 1.5, borderRadius: '10px !important', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: C.amber }} />}>
            <Typography sx={{ fontFamily: F.body, fontWeight: 600, fontSize: 15, color: C.text }}>{faq.q}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography sx={{ fontFamily: F.body, fontSize: 14, color: C.textMid, lineHeight: 1.8 }}>{faq.a}</Typography>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>

    {/* ── CTA ── */}
    <Box component="section" sx={{ background: 'linear-gradient(135deg, #78350f 0%, #92400e 100%)', px: { xs: 2.5, md: 8 }, py: { xs: 6, md: 7 }, textAlign: 'center' }}>
      <Typography component="h2" sx={{ fontFamily: F.heading, fontSize: { xs: 22, md: 30 }, fontWeight: 800, color: C.white, mb: 1.5 }}>
        File Your Income Tax Return Today
      </Typography>
      <Typography sx={{ fontFamily: F.body, fontSize: 15, color: 'rgba(255,255,255,0.7)', mb: 4, maxWidth: 480, mx: 'auto' }}>
        Don't miss the deadline. Our CA team files your ITR accurately — maximising refunds and avoiding penalties.
      </Typography>
      <Button
        component="a"
        href="https://wa.me/9311817126"
        target="_blank"
        rel="noopener noreferrer"
        startIcon={<WhatsAppIcon />}
        sx={{
          background: C.whatsapp,
          color: C.white,
          fontFamily: F.body,
          fontWeight: 700,
          fontSize: 16,
          px: 5,
          py: 1.75,
          borderRadius: 2.5,
          textTransform: 'none',
          boxShadow: SH.btnGreen,
          '&:hover': { background: C.whatsappDk, boxShadow: SH.btnGreenHover },
        }}
      >
        WhatsApp Us Now
      </Button>
    </Box>

    {/* ── Mini Footer ── */}
    <Box component="footer" sx={{ px: { xs: 2.5, md: 8 }, py: 3, background: C.text, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
      <Typography sx={{ fontFamily: F.body, fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>
        © {new Date().getFullYear()} Pracco Corporate Services Pvt. Ltd. — info@pracco.in · +91-9311817126
      </Typography>
      <Link to="/" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: "'DM Sans', sans-serif", fontSize: 13 }}>
        ← Back to Home
      </Link>
    </Box>
  </>
);

export default IncomeTaxFilingPage;
